import * as THREE from 'three';
import { MISSILE } from '../config/weapons';
import { EFFECTS } from '../config/tuning';
import type { Decoy } from '../threat/Countermeasures';
import { DecoyKind } from '../threat/Countermeasures';
import type { Missile } from '../threat/Missile';
import { SMOKE_FRAGMENT, SMOKE_VERTEX } from './shaders/smoke.glsl';
import { ParticleKind, Particles } from './Particles';
import { createSoftPoints, fillConstantColour, type SoftPoints } from './SoftPoints';
import type { TargetInstance } from '../combat/TargetField';

/**
 * Transient visual effects: missile smoke trails and detonation flashes (SPEC §3.6.1, §5.3).
 *
 * **The trail is a gameplay element, not decoration.** SPEC's M6 note is explicit — "smoke trail
 * must be visible at 8 km; tune for readability first, appearance second" — because the column
 * of smoke going up is how the player learns a launch has happened, often before the RWR has
 * anything to say. A trail that faded out at 3 km would remove the single most important warning
 * in the game.
 *
 * That readability requirement is what forces a shader rather than a plain line: the ribbon is
 * expanded in view space to a width with a *screen-space floor*, so a trail 8 km away is still
 * drawn several pixels wide instead of collapsing to a sub-pixel thread that the post stack's
 * FXAA and compression would then erase entirely.
 *
 * This is the seed of the full effects library M7 builds out (explosions, debris, tracers,
 * decals). Everything is pre-allocated: one geometry per system, sized at construction.
 */

/** One trail point: where the missile was, and when. */
interface TrailPoint {
  x: number;
  y: number;
  z: number;
  ageS: number;
}

const cameraScratch = new THREE.Vector3();
const emitPosition = new THREE.Vector3();
const emitVelocity = new THREE.Vector3();
const emitDirection = new THREE.Vector3();

const SMOKE_COLOUR = new THREE.Color(EFFECTS.smokeColourHot);
const FIRE_COLOUR = new THREE.Color(EFFECTS.fireColour);
const FIRE_SMOKE_COLOUR = new THREE.Color(EFFECTS.fireSmokeColour);
const PLUME_COLOUR = new THREE.Color(EFFECTS.plumeColour);
const DEBRIS_COLOUR = new THREE.Color(0x5a5048);

export class Effects {
  readonly object3d = new THREE.Group();

  private readonly trailGeometry: THREE.BufferGeometry;
  private readonly trailPositions: Float32Array;
  private readonly trailSides: Float32Array;
  private readonly trailAges: Float32Array;
  private readonly trailTangents: Float32Array;
  private readonly trailMaterial: THREE.ShaderMaterial;

  /** Per-missile ring buffers of trail points, indexed by the missile's slot. */
  private readonly trails = new Map<number, TrailPoint[]>();
  private readonly trailTimers = new Map<number, number>();

  private readonly flashGeometry: THREE.BufferGeometry;
  private readonly flashPositions: Float32Array;
  private readonly flashAges: Float32Array;
  private readonly flashScales: Float32Array;
  private readonly flashMaterial: THREE.ShaderMaterial;
  private readonly flashSoft: SoftPoints;
  private readonly flashColours: Float32Array;
  private readonly flashSizes: Float32Array;
  private readonly flashParams: Float32Array;
  private flashCount = 0;

  /** Every non-ribbon effect shares one pooled system — see Particles.ts for why. */
  private readonly particles = new Particles();
  /** Fractional particle budget carried between frames, so emission rates are frame-rate free. */
  private fireEmitAccumulator = 0;
  private damageEmitAccumulator = 0;
  private readonly decals: THREE.Mesh[] = [];
  private decalCount = 0;
  private readonly decalGeometry: THREE.CircleGeometry;
  private readonly decalMaterial: THREE.MeshBasicMaterial;

  private readonly decoyGeometry: THREE.BufferGeometry;
  private readonly decoyPositions: Float32Array;
  private readonly decoyMaterial: THREE.ShaderMaterial;
  private readonly decoySoft: SoftPoints;
  private readonly decoyColours: Float32Array;
  private readonly decoySizes: Float32Array;
  private readonly decoyParams: Float32Array;

  constructor() {
    // --- Trails: two vertices per point (left and right of the ribbon), as a triangle strip
    // expanded in the vertex shader.
    const maxVertices = MISSILE.poolCapacity * MISSILE.trailPoints * 2;
    this.trailPositions = new Float32Array(maxVertices * 3);
    this.trailSides = new Float32Array(maxVertices);
    this.trailAges = new Float32Array(maxVertices);
    this.trailTangents = new Float32Array(maxVertices * 3);

    this.trailGeometry = new THREE.BufferGeometry();
    this.trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3));
    this.trailGeometry.setAttribute('aSide', new THREE.BufferAttribute(this.trailSides, 1));
    this.trailGeometry.setAttribute('aAge', new THREE.BufferAttribute(this.trailAges, 1));
    this.trailGeometry.setAttribute('aTangent', new THREE.BufferAttribute(this.trailTangents, 3));

    this.trailMaterial = new THREE.ShaderMaterial({
      vertexShader: SMOKE_VERTEX,
      fragmentShader: SMOKE_FRAGMENT,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        uWidthM: { value: EFFECTS.trailWidthM },
        uMinScreenWidth: { value: EFFECTS.trailMinScreenFraction },
        uLifetimeS: { value: MISSILE.trailPoints * MISSILE.trailIntervalS },
        uColour: { value: new THREE.Color(EFFECTS.smokeColour) },
        uOpacity: { value: EFFECTS.trailOpacity },
      },
    });

    const trailMesh = new THREE.Mesh(this.trailGeometry, this.trailMaterial);
    trailMesh.frustumCulled = false;
    trailMesh.renderOrder = 10;
    this.object3d.add(trailMesh);

    // --- Detonation flashes: expanding points, cheap and legible at range.
    this.flashPositions = new Float32Array(EFFECTS.maxFlashes * 3);
    this.flashAges = new Float32Array(EFFECTS.maxFlashes);
    this.flashScales = new Float32Array(EFFECTS.maxFlashes);
    this.flashGeometry = new THREE.BufferGeometry();
    this.flashGeometry.setAttribute('position', new THREE.BufferAttribute(this.flashPositions, 3));
    // Per-flash size and fade, which `PointsMaterial` could not express: a flash scaled by
    // detonation severity was drawing at a fixed size regardless of the warhead.
    this.flashColours = new Float32Array(EFFECTS.maxFlashes * 3);
    this.flashSizes = new Float32Array(EFFECTS.maxFlashes);
    this.flashParams = new Float32Array(EFFECTS.maxFlashes * 2);
    fillConstantColour(this.flashColours, new THREE.Color(EFFECTS.flashColour));
    this.flashGeometry.setAttribute('color', new THREE.BufferAttribute(this.flashColours, 3));
    this.flashGeometry.setAttribute('size', new THREE.BufferAttribute(this.flashSizes, 1));
    this.flashGeometry.setAttribute('aParams', new THREE.BufferAttribute(this.flashParams, 2));

    this.flashSoft = createSoftPoints({ additive: true });
    this.flashMaterial = this.flashSoft.material;
    const flashPoints = new THREE.Points(this.flashGeometry, this.flashMaterial);
    flashPoints.frustumCulled = false;
    flashPoints.renderOrder = 12;
    this.object3d.add(flashPoints);

    // --- Decoys: chaff clouds and burning flares.
    this.decoyPositions = new Float32Array(EFFECTS.maxDecoys * 3);
    this.decoyGeometry = new THREE.BufferGeometry();
    this.decoyGeometry.setAttribute('position', new THREE.BufferAttribute(this.decoyPositions, 3));
    this.decoyColours = new Float32Array(EFFECTS.maxDecoys * 3);
    this.decoySizes = new Float32Array(EFFECTS.maxDecoys);
    this.decoyParams = new Float32Array(EFFECTS.maxDecoys * 2);
    fillConstantColour(this.decoyColours, new THREE.Color(EFFECTS.flareColour));
    for (let i = 0; i < EFFECTS.maxDecoys; i += 1) {
      this.decoySizes[i] = EFFECTS.decoySizeM;
      this.decoyParams[i * 2] = 1;
      // A flare is a burning point source: soft, with a bright core.
      this.decoyParams[i * 2 + 1] = 0.85;
    }
    this.decoyGeometry.setAttribute('color', new THREE.BufferAttribute(this.decoyColours, 3));
    this.decoyGeometry.setAttribute('size', new THREE.BufferAttribute(this.decoySizes, 1));
    this.decoyGeometry.setAttribute('aParams', new THREE.BufferAttribute(this.decoyParams, 2));

    this.decoySoft = createSoftPoints({ additive: true });
    this.decoyMaterial = this.decoySoft.material;
    const decoyPoints = new THREE.Points(this.decoyGeometry, this.decoyMaterial);
    decoyPoints.frustumCulled = false;
    decoyPoints.renderOrder = 11;
    this.object3d.add(decoyPoints);

    this.object3d.add(this.particles.object3d);

    // --- Ground scorch decals. Flat discs laid on the terrain, pooled and reused oldest-first.
    this.decalGeometry = new THREE.CircleGeometry(EFFECTS.decalRadiusM, 16);
    this.decalGeometry.rotateX(-Math.PI * 0.5);
    this.decalMaterial = new THREE.MeshBasicMaterial({
      color: 0x1a1613,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });
    for (let i = 0; i < EFFECTS.maxDecals; i += 1) {
      const decal = new THREE.Mesh(this.decalGeometry, this.decalMaterial);
      decal.visible = false;
      decal.renderOrder = 2;
      this.decals.push(decal);
      this.object3d.add(decal);
    }
  }

  /** Live particle count, for the debug overlay's budget line. */
  get particleCount(): number {
    return this.particles.liveCount;
  }

  /**
   * A full explosion (SPEC §5.3): flash, expanding smoke ball, debris shower, and a ground dust
   * ring when it happens near the surface.
   *
   * `groundY` is the surface height beneath the blast; passing the blast's own height suppresses
   * the ground interaction, which is what an airburst should look like.
   */
  explode(positionM: THREE.Vector3, severity: number, groundY: number): void {
    const scale = 0.6 + severity * 0.8;
    this.detonate(positionM, severity);

    emitDirection.set(0, 1, 0);
    this.particles.burst(
      positionM,
      emitDirection,
      EFFECTS.debrisSpeedMs * 0.4 * scale,
      Math.round(EFFECTS.explosionSmoke * scale),
      1,
      EFFECTS.explosionLifetimeS,
      EFFECTS.smokeBallSizeM * scale,
      SMOKE_COLOUR,
      ParticleKind.Smoke,
    );
    this.particles.burst(
      positionM,
      emitDirection,
      EFFECTS.debrisSpeedMs * scale,
      Math.round(EFFECTS.explosionDebris * scale),
      1,
      EFFECTS.debrisLifetimeS,
      EFFECTS.debrisSizeM,
      DEBRIS_COLOUR,
      ParticleKind.Debris,
    );

    // Ground interaction: a dust ring thrown outward, and a scorch mark left behind.
    if (positionM.y - groundY < EFFECTS.decalRadiusM) {
      emitPosition.set(positionM.x, groundY + 0.6, positionM.z);
      emitDirection.set(0, 0.18, 0);
      this.particles.burst(
        emitPosition,
        emitDirection,
        EFFECTS.debrisSpeedMs * 0.5 * scale,
        14,
        0.95,
        1.6,
        EFFECTS.smokeBallSizeM * 0.5 * scale,
        SMOKE_COLOUR,
        ParticleKind.Smoke,
      );
      this.scorch(emitPosition, scale);
    }
  }

  /** Water impact (SPEC §5.3): a column plume and an expanding ring. */
  waterImpact(positionM: THREE.Vector3, severity: number): void {
    const scale = 0.7 + severity;
    emitPosition.set(positionM.x, 0.5, positionM.z);
    emitDirection.set(0, 1, 0);
    this.particles.burst(
      emitPosition,
      emitDirection,
      EFFECTS.plumeSpeedMs * scale,
      EFFECTS.plumeParticles,
      0.35,
      2.2,
      EFFECTS.plumeSizeM * scale,
      PLUME_COLOUR,
      ParticleKind.Plume,
    );
    // The ring wave: a flat, fast, wide spray.
    emitDirection.set(0, 0.1, 0);
    this.particles.burst(
      emitPosition,
      emitDirection,
      EFFECTS.plumeSpeedMs * 0.7,
      14,
      0.95,
      1.4,
      EFFECTS.plumeSizeM * 0.6,
      PLUME_COLOUR,
      ParticleKind.Plume,
    );
  }

  /** Lay a scorch decal on the ground, reusing the oldest slot when they run out. */
  private scorch(positionM: THREE.Vector3, scale: number): void {
    const decal = this.decals[this.decalCount % EFFECTS.maxDecals];
    this.decalCount += 1;
    decal.position.set(positionM.x, positionM.y + 0.15, positionM.z);
    decal.scale.setScalar(Math.max(0.4, scale));
    decal.visible = true;
  }

  /** Record a detonation or self-destruct. `severity` scales the flash; 0 is a clean miss. */
  detonate(positionM: THREE.Vector3, severity: number): void {
    if (this.flashCount >= EFFECTS.maxFlashes) return;
    const index = this.flashCount;
    this.flashCount += 1;
    this.flashPositions[index * 3] = positionM.x;
    this.flashPositions[index * 3 + 1] = positionM.y;
    this.flashPositions[index * 3 + 2] = positionM.z;
    this.flashAges[index] = 0;
    this.flashScales[index] = 1 + severity * 2;
    this.flashSizes[index] = EFFECTS.flashSizeM * this.flashScales[index] * 0.45;
    this.flashParams[index * 2] = 1;
    this.flashParams[index * 2 + 1] = 1;
  }

  /**
   * Rebuild the effect geometry for this frame.
   *
   * Called from the render loop, not the tick: trails are presentation. Missile positions are
   * interpolated by `alpha` for the same reason everything else is (SPEC §4.3) — an uninterpolated
   * trail at 800 m/s would visibly step.
   */
  update(
    missiles: readonly Missile[],
    decoys: readonly Decoy[],
    dtS: number,
    alpha: number,
  ): void {
    this.updateTrails(missiles, dtS, alpha);
    this.updateFlashes(dtS);
    this.updateDecoys(decoys);
    this.particles.update(dtS);
  }

  /**
   * Sustained secondary fires on destroyed targets (SPEC §5.3): a fire column with rising smoke,
   * burning for the target's own persistence — up to half a minute for a fuel farm.
   */
  updateFires(targets: readonly TargetInstance[], dtS: number): void {
    let burning = 0;
    for (let i = 0; i < targets.length; i += 1) {
      if (targets[i].fireRemainingS > 0) burning += 1;
    }
    if (burning === 0) {
      this.fireEmitAccumulator = 0;
      return;
    }

    this.fireEmitAccumulator += EFFECTS.fireEmitPerS * burning * dtS;
    const toEmit = Math.floor(this.fireEmitAccumulator);
    this.fireEmitAccumulator -= toEmit;

    for (let n = 0; n < toEmit; n += 1) {
      // Round-robin across the burning targets so one fire does not starve the others.
      let index = n % burning;
      for (let i = 0; i < targets.length; i += 1) {
        if (targets[i].fireRemainingS <= 0) continue;
        if (index > 0) {
          index -= 1;
          continue;
        }
        const target = targets[i];
        emitPosition.copy(target.positionM);
        emitPosition.y += 1.5;
        emitDirection.set(0, 1, 0);
        // Flame at the base, smoke above it: two emitters, one column.
        const hot = n % 3 !== 0;
        this.particles.burst(
          emitPosition,
          emitDirection,
          hot ? 6 : 3,
          1,
          0.5,
          hot ? 1.5 : 3.4,
          hot ? EFFECTS.fireSizeM : EFFECTS.fireSizeM * 1.4,
          hot ? FIRE_COLOUR : FIRE_SMOKE_COLOUR,
          hot ? ParticleKind.Fire : ParticleKind.Smoke,
        );
        break;
      }
    }
  }

  /**
   * Smoke trailing from a wounded airframe (SPEC §5.3), scaled by damage.
   *
   * This is the player's own damage made visible from the outside — invisible from a nose camera,
   * but the thing that makes the impact cam and the debrief read as a story rather than a result.
   */
  updateDamageSmoke(
    positionM: THREE.Vector3,
    velocityMs: THREE.Vector3,
    damage: number,
    dtS: number,
  ): void {
    if (damage < EFFECTS.damageSmokeThreshold) {
      this.damageEmitAccumulator = 0;
      return;
    }

    const severity = (damage - EFFECTS.damageSmokeThreshold) / (1 - EFFECTS.damageSmokeThreshold);
    this.damageEmitAccumulator += EFFECTS.damageSmokeEmitPerS * severity * dtS;
    const toEmit = Math.floor(this.damageEmitAccumulator);
    this.damageEmitAccumulator -= toEmit;

    for (let i = 0; i < toEmit; i += 1) {
      emitVelocity.copy(velocityMs).multiplyScalar(0.25);
      this.particles.emit(
        positionM,
        emitVelocity,
        EFFECTS.damageSmokeLifetimeS,
        EFFECTS.damageSmokeSizeM * (0.5 + severity),
        SMOKE_COLOUR,
        ParticleKind.Smoke,
      );
    }
  }

  reset(): void {
    this.trails.clear();
    this.trailTimers.clear();
    this.flashCount = 0;
    this.particles.clear();
    this.fireEmitAccumulator = 0;
    this.damageEmitAccumulator = 0;
    for (let i = 0; i < this.decals.length; i += 1) this.decals[i].visible = false;
    this.decalCount = 0;
    this.trailGeometry.setDrawRange(0, 0);
    this.flashGeometry.setDrawRange(0, 0);
    this.decoyGeometry.setDrawRange(0, 0);
  }

  dispose(): void {
    this.particles.dispose();
    this.decalGeometry.dispose();
    this.decalMaterial.dispose();
    this.trailGeometry.dispose();
    this.trailMaterial.dispose();
    this.flashGeometry.dispose();
    // `SoftPoints` owns the material and its resize subscription; disposing the material alone
    // would leave a listener holding this object alive.
    this.flashSoft.dispose();
    this.decoyGeometry.dispose();
    this.decoySoft.dispose();
  }

  private updateTrails(missiles: readonly Missile[], dtS: number, alpha: number): void {
    // Age every trail, including those whose missile has gone: a smoke column outlives the round
    // that drew it, which is exactly the cue the player is reading.
    for (const [, points] of this.trails) {
      for (let i = 0; i < points.length; i += 1) points[i].ageS += dtS;
    }

    const live = new Set<number>();
    for (let i = 0; i < missiles.length; i += 1) {
      const missile = missiles[i];
      live.add(missile.id);

      let points = this.trails.get(missile.id);
      if (points === undefined) {
        points = [];
        this.trails.set(missile.id, points);
        this.trailTimers.set(missile.id, 0);
      }

      const timer = (this.trailTimers.get(missile.id) ?? 0) + dtS;
      if (timer >= MISSILE.trailIntervalS) {
        this.trailTimers.set(missile.id, 0);
        cameraScratch.copy(missile.previousPositionM).lerp(missile.positionM, alpha);
        points.push({ x: cameraScratch.x, y: cameraScratch.y, z: cameraScratch.z, ageS: 0 });
        if (points.length > MISSILE.trailPoints) points.shift();
      } else {
        this.trailTimers.set(missile.id, timer);
      }
    }

    // Retire trails that have fully faded.
    const lifetimeS = MISSILE.trailPoints * MISSILE.trailIntervalS;
    for (const [id, points] of this.trails) {
      if (live.has(id)) continue;
      while (points.length > 0 && points[0].ageS > lifetimeS) points.shift();
      if (points.length === 0) {
        this.trails.delete(id);
        this.trailTimers.delete(id);
      }
    }

    this.writeTrailGeometry();
  }

  /** Two vertices per point; the shader expands them sideways in view space. */
  private writeTrailGeometry(): void {
    let vertex = 0;
    const maxVertices = this.trailSides.length;

    for (const [, points] of this.trails) {
      if (points.length < 2) continue;
      for (let i = 0; i < points.length && vertex + 1 < maxVertices; i += 1) {
        const point = points[i];
        // Tangent toward the next point, or back from the previous one at the end of the trail.
        const ahead = points[Math.min(i + 1, points.length - 1)];
        const behind = points[Math.max(i - 1, 0)];
        const tx = ahead.x - behind.x;
        const ty = ahead.y - behind.y;
        const tz = ahead.z - behind.z;
        for (let side = 0; side < 2; side += 1) {
          this.trailTangents[vertex * 3] = tx;
          this.trailTangents[vertex * 3 + 1] = ty;
          this.trailTangents[vertex * 3 + 2] = tz;
          this.trailPositions[vertex * 3] = point.x;
          this.trailPositions[vertex * 3 + 1] = point.y;
          this.trailPositions[vertex * 3 + 2] = point.z;
          this.trailSides[vertex] = side === 0 ? -1 : 1;
          this.trailAges[vertex] = point.ageS;
          vertex += 1;
        }
      }
      // Degenerate pair between trails so the strip does not join one missile's smoke to another's.
      if (vertex + 1 < maxVertices && points.length >= 2) {
        const last = points[points.length - 1];
        for (let side = 0; side < 2; side += 1) {
          this.trailTangents[vertex * 3] = 0;
          this.trailTangents[vertex * 3 + 1] = 0;
          this.trailTangents[vertex * 3 + 2] = 0;
          this.trailPositions[vertex * 3] = last.x;
          this.trailPositions[vertex * 3 + 1] = last.y;
          this.trailPositions[vertex * 3 + 2] = last.z;
          this.trailSides[vertex] = 0;
          this.trailAges[vertex] = 1e9;
          vertex += 1;
        }
      }
    }

    this.trailGeometry.attributes['position'].needsUpdate = true;
    this.trailGeometry.attributes['aSide'].needsUpdate = true;
    this.trailGeometry.attributes['aAge'].needsUpdate = true;
    this.trailGeometry.attributes['aTangent'].needsUpdate = true;
    this.trailGeometry.setDrawRange(0, vertex);
  }

  private updateFlashes(dtS: number): void {
    for (let i = this.flashCount - 1; i >= 0; i -= 1) {
      this.flashAges[i] += dtS;
      if (this.flashAges[i] >= EFFECTS.flashLifetimeS) {
        // Swap-remove.
        const last = this.flashCount - 1;
        for (let axis = 0; axis < 3; axis += 1) {
          this.flashPositions[i * 3 + axis] = this.flashPositions[last * 3 + axis];
        }
        this.flashAges[i] = this.flashAges[last];
        this.flashScales[i] = this.flashScales[last];
        this.flashCount -= 1;
        continue;
      }

      // A flash expands fast and dies fast — that shape is most of what reads as a detonation
      // rather than as a light being switched on. Both terms were previously computed into
      // arrays the material could not see.
      const t = this.flashAges[i] / EFFECTS.flashLifetimeS;
      this.flashSizes[i] = EFFECTS.flashSizeM * this.flashScales[i] * (0.45 + t * 1.5);
      this.flashParams[i * 2] = (1 - t) * (1 - t);
      this.flashParams[i * 2 + 1] = 1;
    }
    this.flashGeometry.attributes['position'].needsUpdate = true;
    this.flashGeometry.attributes['size'].needsUpdate = true;
    this.flashGeometry.attributes['aParams'].needsUpdate = true;
    this.flashGeometry.setDrawRange(0, this.flashCount);
  }

  private updateDecoys(decoys: readonly Decoy[]): void {
    let count = 0;
    for (let i = 0; i < decoys.length && count < EFFECTS.maxDecoys; i += 1) {
      const decoy = decoys[i];
      if (!decoy.active) continue;
      this.decoyPositions[count * 3] = decoy.positionM.x;
      this.decoyPositions[count * 3 + 1] = decoy.positionM.y;
      this.decoyPositions[count * 3 + 2] = decoy.positionM.z;
      count += 1;
      // Chaff and flares share a buffer; the flare colour dominates, which is honest enough at
      // the ranges these are seen from. M7 splits them when the effects library lands.
      void DecoyKind;
    }
    this.decoyGeometry.attributes['position'].needsUpdate = true;
    this.decoyGeometry.setDrawRange(0, count);
  }
}
